import React, { useEffect, useState } from "react";
import { applyAdminTheme } from "../utils/adminTheme";
import { isInIframe } from "../utils/isInIframe";

/**
 * Sends ourselves the body classes the Shoptet admin would send, so the themes can be walked through
 * locally without an admin around it. Only in development, and only when we are not embedded — inside
 * an iframe the real messages drive the classes and a second hand on the wheel would fight them.
 *
 * The choice is remembered so it survives a reload, which is what makes walking the wizard bearable.
 * Styled with fixed colours on purpose: a switch for the theme has to stay legible in every theme.
 */

const STORAGE_KEY = "shoptet_dev_theme";

type DevTheme = { brand: boolean; dark: boolean };

const readStored = (): DevTheme => {
  try {
    return { brand: false, dark: false, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") };
  } catch {
    return { brand: false, dark: false };
  }
};

const buttonStyle = (active: boolean): React.CSSProperties => ({
  background: active ? "#e6007e" : "#3a3a3a",
  border: active ? "1px solid #ff4fae" : "1px solid #5a5a5a",
  borderRadius: "4px",
  color: "#ffffff",
  cursor: "pointer",
  font: "inherit",
  fontSize: "13px",
  fontWeight: 600,
  padding: "6px 14px",
});

export const DevThemeSwitcher = () => {
  const [theme, setTheme] = useState<DevTheme>(readStored);
  const enabled = process.env.NODE_ENV === "development" && !isInIframe();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // `iframe-section` is always on when the admin embeds us, so the simulation keeps it on too
    applyAdminTheme({ "iframe-section": true, "theme-brand": theme.brand, "theme-dark": theme.dark });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  }, [enabled, theme]);

  if (!enabled) {
    return null;
  }

  return (
    <div
      style={{
        alignItems: "center",
        background: "#201f1d",
        border: "2px solid #e6007e",
        borderRadius: "6px",
        bottom: "16px",
        boxShadow: "0 4px 16px rgb(0 0 0 / 40%)",
        color: "#f1f0ee",
        display: "flex",
        fontSize: "13px",
        gap: "8px",
        left: "16px",
        padding: "10px 12px",
        position: "fixed",
        zIndex: 2147483647,
      }}
    >
      <span style={{ fontWeight: 600, letterSpacing: "0.4px", textTransform: "uppercase" }}>dev · admin theme</span>
      <button
        style={buttonStyle(theme.dark)}
        type="button"
        onClick={() => setTheme((current) => ({ ...current, dark: !current.dark }))}
      >
        dark
      </button>
      <button
        style={buttonStyle(theme.brand)}
        type="button"
        onClick={() => setTheme((current) => ({ ...current, brand: !current.brand }))}
      >
        brand
      </button>
    </div>
  );
};
